
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Navigation from '../../components/feature/Navigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Settings() {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleExportData = () => {
    const jobs = JSON.parse(localStorage.getItem('apostille_jobs') || '[]');
    const dataStr = JSON.stringify(jobs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `apostille-jobs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string);
          if (Array.isArray(importedData)) {
            localStorage.setItem('apostille_jobs', JSON.stringify(importedData));
            alert('Data imported successfully!');
            window.location.reload();
          } else {
            alert('Invalid file format');
          }
        } catch (error) {
          alert('Error reading file');
        }
      };
      reader.readAsText(file);
    }
    setShowImportModal(false);
  };

  const handleClearData = () => {
    if (confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      localStorage.removeItem('apostille_jobs');
      alert('All data has been cleared');
      window.location.reload();
    }
  };

  const settingsItems = [
    {
      title: 'Export Data',
      description: 'Download all your job data as JSON file',
      icon: 'ri-download-line',
      action: () => setShowExportModal(true)
    },
    {
      title: 'Import Data',
      description: 'Import job data from JSON file',
      icon: 'ri-upload-line',
      action: () => setShowImportModal(true)
    },
    {
      title: 'Clear All Data',
      description: 'Remove all jobs and client data',
      icon: 'ri-delete-bin-line',
      action: handleClearData,
      danger: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Settings" />
      
      <div className="pt-16 pb-20 px-4">
        {/* App Info */}
        <Card className="p-4 mb-6">
          <div className="text-center">
            <img
              src="https://public.readdy.ai/ai/img_res/b84b14ead8974d783ad087e52b94e337.jpg"
              alt="App Logo"
              className="w-16 h-16 mx-auto mb-3 rounded-lg"
            />
            <h2 className="text-lg font-semibold text-gray-900">Apostille Tracker</h2>
            <p className="text-sm text-gray-600">Version 1.0.0</p>
            <p className="text-xs text-gray-500 mt-2">
              Professional apostille document tracking system
            </p>
          </div>
        </Card>

        {/* Settings Options */}
        <div className="space-y-4">
          {settingsItems.map((item, index) => (
            <Card key={index} className="p-4" onClick={item.action}>
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  item.danger ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  <i className={`${item.icon} text-lg ${
                    item.danger ? 'text-red-600' : 'text-blue-600'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    item.danger ? 'text-red-900' : 'text-gray-900'
                  }`}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </div>
            </Card>
          ))}
        </div>

        {/* About */}
        <Card className="p-4 mt-6">
          <h3 className="font-medium text-gray-900 mb-2">About</h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Apostille Tracker helps you manage and track apostille services efficiently. 
            Keep track of client information, document processing status, shipping details, 
            and payment information all in one place.
          </p>
        </Card>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will download all your job data as a JSON file that you can import later.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowExportModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExportData}
                className="flex-1"
              >
                Export
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Data</h3>
            <p className="text-sm text-gray-600 mb-6">
              Select a JSON file to import job data. This will replace all existing data.
            </p>
            <input
              type="file"
              accept=".json"
              onChange={handleImportData}
              className="hidden"
              id="import-file"
            />
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowImportModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => document.getElementById('import-file')?.click()}
                className="flex-1"
              >
                Select File
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Navigation />
    </div>
  );
}
